import { type ComponentProps } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  children: string;
  tone?: 'amber' | 'neutral';
}

export function Markdown({ children, tone = 'neutral' }: Props) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeComponents(tone)}>
      {children}
    </ReactMarkdown>
  );
}

function makeComponents(tone: 'amber' | 'neutral'): Components {
  const text = tone === 'amber' ? 'text-amber-100/95' : 'text-neutral-100';
  const heading = tone === 'amber' ? 'text-amber-50' : 'text-neutral-50';
  const inlineCode =
    tone === 'amber'
      ? 'text-amber-200 bg-amber-950/50'
      : 'text-amber-200 bg-neutral-800';
  const codeBg = tone === 'amber' ? 'bg-black/40 text-amber-50' : 'bg-black/50 text-neutral-100';
  const accent = tone === 'amber' ? 'border-amber-500/50' : 'border-neutral-600';
  const link = tone === 'amber' ? 'text-amber-300' : 'text-blue-400';

  return {
    h1: (p) => <h1 className={`text-base md:text-lg 2xl:text-xl font-semibold mt-3 mb-1.5 ${heading}`} {...stripNode(p)} />,
    h2: (p) => <h2 className={`text-sm md:text-base 2xl:text-lg font-semibold mt-3 mb-1 ${heading}`} {...stripNode(p)} />,
    h3: (p) => <h3 className={`text-sm md:text-base 2xl:text-lg font-semibold mt-2.5 mb-1 ${text}`} {...stripNode(p)} />,
    h4: (p) => <h4 className={`text-[13px] md:text-base 2xl:text-lg font-semibold mt-2 mb-1 ${text}`} {...stripNode(p)} />,
    p: (p) => <p className={`my-1.5 text-[13px] md:text-base 2xl:text-lg leading-relaxed ${text}`} {...stripNode(p)} />,
    ul: (p) => <ul className={`list-disc pl-5 my-1.5 space-y-0.5 text-[13px] md:text-base 2xl:text-lg ${text}`} {...stripNode(p)} />,
    ol: (p) => <ol className={`list-decimal pl-5 my-1.5 space-y-0.5 text-[13px] md:text-base 2xl:text-lg ${text}`} {...stripNode(p)} />,
    li: (p) => <li className="my-0.5" {...stripNode(p)} />,
    strong: (p) => <strong className={`font-semibold ${heading}`} {...stripNode(p)} />,
    em: (p) => <em className="italic" {...stripNode(p)} />,
    code: (props: ComponentProps<'code'> & { node?: unknown; inline?: boolean }) => {
      const { node: _n, inline, className, children, ...rest } = props;
      if (inline) {
        return (
          <code
            className={`font-mono text-[12px] px-1 py-px rounded ${inlineCode}`}
            {...rest}
          >
            {children}
          </code>
        );
      }
      return (
        <code className={`font-mono text-[12px] ${className ?? ''}`} {...rest}>
          {children}
        </code>
      );
    },
    pre: (p) => (
      <pre
        className={`font-mono text-[12px] rounded-lg p-2 my-2 overflow-auto whitespace-pre-wrap break-words ${codeBg}`}
        {...stripNode(p)}
      />
    ),
    blockquote: (p) => (
      <blockquote
        className={`border-l-2 pl-3 my-1.5 italic ${accent} ${text}`}
        {...stripNode(p)}
      />
    ),
    a: (p) => <a className={`underline ${link}`} target="_blank" rel="noreferrer" {...stripNode(p)} />,
    hr: () => <hr className={`my-3 border ${accent}`} />,
    table: (p) => (
      <div className="overflow-auto my-2">
        <table className="text-[12px] border-collapse" {...stripNode(p)} />
      </div>
    ),
    th: (p) => (
      <th className={`border ${accent} px-2 py-0.5 text-left font-semibold`} {...stripNode(p)} />
    ),
    td: (p) => <td className={`border ${accent} px-2 py-0.5`} {...stripNode(p)} />,
  };
}

function stripNode<T extends { node?: unknown }>(props: T): Omit<T, 'node'> {
  const { node: _n, ...rest } = props;
  return rest;
}
